import { SDKCore, SDKEnv, SDKStatusCode } from "@nebula-sdk/core";
import { SDKBrower } from "./enum";
import httpRequest from "./http";
import localStorage from "./storage";
import type { WEBSDKCoreScopeState, WEBSDKCoreTrackArgs, WEBSDKCoreTrackResult } from "./type";
import type {
  SDKInitArgs, SDKInitResult, SDKRequest, SDKStorage, SDKValidateArgs, SDKValidateResult, SDKPlatform,
  SDKRequestOptions, SDKResponseOptions,
} from "@nebula-sdk/core";

// 网页SDK核心
export class NebulaWEBGameSDKCore extends SDKCore {
  // 本地存储
  public readonly localStorage: SDKStorage = localStorage;

  // 网络请求
  public readonly httpRequest: SDKRequest = httpRequest;

  // 环境支持
  public get support(): boolean {
    if (typeof bl !== "undefined") return false;
    if (typeof jd !== "undefined") return false;
    if (typeof ks !== "undefined") return false;
    if (typeof mt !== "undefined") return false;
    if (typeof my !== "undefined") return false;
    if (typeof qg !== "undefined") return false;
    if (typeof tt !== "undefined") return false;
    if (typeof wx !== "undefined") return false;
    if (typeof window === "undefined") return false;
    if (typeof window.localStorage === "undefined") return false;
    if (typeof window.navigator === "undefined") return false;
    if (typeof window.document === "undefined") return false;
    if (typeof window.XMLHttpRequest === "undefined") return false;
    return true;
  }

  // 运行环境
  public get env(): SDKEnv {
    return SDKEnv.Web;
  }

  // 运行平台
  public get platform(): SDKPlatform {
    if (/android/i.test(window.navigator.userAgent)) return "android" as SDKPlatform;
    if (/ip(?:hone|od|ad)/i.test(window.navigator.userAgent)) return "ios" as SDKPlatform;
    if (/windows/i.test(window.navigator.userAgent)) return "windows" as SDKPlatform;
    if (/macintosh|mac os x/i.test(window.navigator.userAgent)) return "mac" as SDKPlatform;
    return "unknown" as SDKPlatform;
  }

  // 运行浏览器
  public get browser(): SDKBrower {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.includes("micromessenger")) return SDKBrower.WeChat;
    if (userAgent.includes("alipayclient")) return SDKBrower.Alipay;
    if (userAgent.includes("qq")) return SDKBrower.QQ;
    if (userAgent.includes("quark")) return SDKBrower.Quark;
    if (userAgent.includes("aweme")) return SDKBrower.Douyin;
    if (userAgent.includes("weibo")) return SDKBrower.Weibo;
    return SDKBrower.Unknown;
  }

  // 核心状态
  private readonly scopeState: WEBSDKCoreScopeState = {
    templateCode: "nbwebpay",
    deviceType: "unknown",
    networkType: "unknown",
    networkTypes: { "2g": 0, "3g": 0, "4g": 2, "5g": 3, "wifi": 1, "unknown": 0, "none": 0 },
    osVersion: "unknown",
  };

  // 内部校验
  public async validate(args: SDKValidateArgs): Promise<SDKValidateResult> {
    const result = await super.validate(args);
    if (result.status === "error") {
      void this.scopeRequestTrack({
        eventId: "validate",
        eventName: "SDK内部校验失败",
        eventGroup: "core",
        eventValue: { args, result },
        eventLevel: "error",
      });
    }
    return result;
  }

  // 初始化
  public async init(args: SDKInitArgs): Promise<SDKInitResult> {
    // 验证环境支持状态
    const result = await this.validate(["support"]);
    if (result.status === "error") return result;

    const navigator = window.navigator as Navigator & {
      connection?: { effectiveType?: string };
      userAgentData?: { platform?: string };
    };
    // 更新设备信息
    this.scopeState.deviceType = navigator.userAgentData?.platform ?? "unknown";
    this.scopeState.osVersion = navigator.userAgent;
    // 更新网络信息
    this.scopeState.networkType = navigator.connection?.effectiveType ?? "unknown";

    // 初始化渠道配置
    if (Array.isArray(args.config)) {
      const config = args.config.find((item) => {
        return item.nbChannelTemplateCode === this.scopeState.templateCode;
      });
      this.options = config ? { version: args.version, debug: args.debug, config } : null;
    } else {
      this.options = { version: args.version, debug: args.debug, config: args.config };
    }

    // 检查配置是否存在
    if (!this.options) {
      void this.scopeRequestTrack({
        eventId: "init",
        eventName: "SDK未找到网页渠道配置",
        eventGroup: "core",
        eventValue: { args, result: "未找到网页渠道配置" },
        eventLevel: "error",
      });
      return { status: "error", code: SDKStatusCode.Error, msg: "未找到网页渠道配置", data: null };
    }

    return {
      status: "success",
      code: SDKStatusCode.Success,
      msg: "初始化网页渠道配置成功",
      data: this.options.config,
    };
  }

  // 日志上报
  public async scopeRequestTrack(args: WEBSDKCoreTrackArgs): Promise<WEBSDKCoreTrackResult> {
    return await this.track({
      appId: this.options?.config.nbAppId ?? "",
      appVersion: this.version,
      appChannel: this.env,
      userId: this.userInfo?.nbOpenId ?? "",
      userTag: this.userInfo?.openId ?? "",
      deviceId: this.deviceId,
      deviceModel: this.scopeState.deviceType,
      osType: this.platform,
      osVersion: this.scopeState.osVersion,
      networkType: this.scopeState.networkType,
      eventId: args.eventId,
      eventName: args.eventName,
      eventGroup: args.eventGroup ?? "",
      eventValue: args.eventValue,
      eventTime: this.formatDateTime(Date.now()),
      eventLevel: args.eventLevel ?? "info",
    });
  }

  // 通行证签名请求
  public async scopeRequestPassport<T>(args: SDKRequestOptions): Promise<SDKResponseOptions<T>> {
    const result = await this.requestPassport<T>({ ...args });

    void this.scopeRequestTrack({
      eventId: args.url,
      eventName: "通行证请求",
      eventGroup: "core",
      eventValue: { args, result },
      eventLevel: result.status === "error" ? "error" : "info",
    });
    return result;
  }
}
